import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import FlexColumn from 'ember-simplepractice/components/spds/layout/flex/column';
import FlexRow from 'ember-simplepractice/components/spds/layout/flex/row';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import LoadingSpinner from 'ember-simplepractice/components/spds/indicator/loading-spinner';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import SecondaryButton from 'ember-simplepractice/components/spds/action/button/secondary';
import SpdsFeedbackAlertBannerMessage from 'ember-simplepractice/components/spds/feedback/alert-banner/message';
import TextInput from 'ember-simplepractice/components/spds/input/text';
import styles from './conversation-display-name.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const DISPLAY_NAME_CHAR_LIMIT = 100;

export default class ConversationDisplayName extends Component {
  @tracked displayName;
  @tracked hasSaveError = false;
  @tracked isEditing = false;

  get shouldRender() {
    return this.args.editable || this.args.displayName;
  }

  edit = () => {
    this.displayName = this.args.displayName;
    this.hasSaveError = false;
    this.isEditing = true;
  };

  cancel = () => {
    this.displayName = this.args.displayName;
    this.hasSaveError = false;
    this.isEditing = false;
  };

  save = task({ drop: true }, async () => {
    this.hasSaveError = false;

    try {
      await this.args.onSave?.((this.displayName ?? '').trim());
      this.isEditing = false;
    } catch {
      this.hasSaveError = true;
    }
  });

  onSave = () => {
    this.save.perform();
  };

  updateDisplayName = value => {
    this.displayName = value.target.value;
  };

  <template>
    {{#if this.shouldRender}}
      <div class={{styles.component}} data-test-conversation-display-name>
        <h2 class="section-title" data-test-display-name-section-title>
          {{t "emc.conversation_details.display_name.section_title"}}
        </h2>

        <FlexRow @alignItems="center" @gap="0.25rem">
          {{#if this.isEditing}}
            <FlexColumn @flexGrow="1" @gap="0.75rem" data-test-is-editing>
              <TextInput
                @charLimit={{DISPLAY_NAME_CHAR_LIMIT}}
                @disabled={{this.save.isRunning}}
                @placeholder={{t "emc.conversation_details.display_name.placeholder"}}
                @onInput={{this.updateDisplayName}}
                @value={{this.displayName}}
                data-test-display-name-input
              />
              <FlexRow @gap="8px" @justifyContent="flex-end">
                <SecondaryButton
                  @disabled={{this.save.isRunning}}
                  @text={{t "emc.buttons.cancel"}}
                  {{on "click" this.cancel}}
                  data-test-display-name-cancel
                />
                <PrimaryButton
                  @disabled={{this.save.isRunning}}
                  {{on "click" this.onSave}}
                  data-test-display-name-save
                >
                  <span class="save-button-container">
                    <span class={{if this.save.isRunning "is-hidden"}}>
                      {{t "emc.buttons.save"}}
                    </span>
                    {{#if this.save.isRunning}}
                      <LoadingSpinner
                        class="save-button-spinner"
                        @color="inverse"
                        @size="small"
                        data-test-display-name-saving
                      />
                    {{/if}}
                  </span>
                </PrimaryButton>
              </FlexRow>

              {{#if this.hasSaveError}}
                <SpdsFeedbackAlertBannerMessage
                  @body={{t "emc.conversation_details.display_name.save_error"}}
                  @includeIcon={{true}}
                  @type="error"
                  data-test-display-name-save-error
                />
              {{/if}}
            </FlexColumn>
          {{else if @editable}}
            {{#if @displayName}}
              <span class="display-name" data-test-display-name-value>{{@displayName}}</span>
              <GhostButton
                @iconOnly={{true}}
                @inline={{true}}
                {{on "click" this.edit}}
                data-test-display-name-edit-button
              >
                {{svgJar "messaging-compose"}}
              </GhostButton>
            {{else}}
              <GhostButton
                class="name-conversation-button"
                {{on "click" this.edit}}
                data-test-name-conversation-button
              >
                {{svgJar "messaging-compose"}}
                {{t "emc.conversation_details.display_name.cta_text"}}
              </GhostButton>
            {{/if}}
          {{else}}
            <span data-test-read-only-display-name>{{@displayName}}</span>
          {{/if}}
        </FlexRow>
      </div>
    {{/if}}
  </template>
}
